import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';

import { parkCreationRequestPins } from '../../api/url_data_agent_evaluations';
import {
  EMPTY_PARK_REPORT,
  MAX_SOP_CHARS,
  addParkResult,
  chunkPinIds,
  getParsedPinsNotes,
  parsePinIds,
  toCreationBatchName,
  toCreationBatchTag
} from '../../helpers/url_data_agent_creation_requests_helpers';
import { formatDate, formatNumber } from '../../helpers/url_data_agent_evaluations_helpers';
import CreationRequestParkReport from './CreationRequestParkReport';

import './CreationRequestStartForm.scss';

const MODES = [
  { createWhenReady: false, label: 'Review first', description: 'APM prepares each product, and a person approves it here before APM creates it.' },
  { createWhenReady: true, label: 'Create when ready', description: 'APM creates each product as soon as it has read the page; you can stop it from the batch.' }
];

/**
 * A new creation batch: its pins, SOP and mode, a Check of what parking them would do and cost, then a Start that parks them
 * 1,000 at a time under one tag and resumes from a chunk that failed.
 * @param {Object[]} batches — the loaded batches, whose SOPs a new batch can start from
 * @param {Object|null} lane — APM's room and cost from the batch list, which price and time the check
 */
const CreationRequestStartForm = ({ batches, lane, onClose, onOpenBatch }) => {
  const [name, setName] = useState('');
  const [pinText, setPinText] = useState('');
  const [sop, setSop] = useState('');
  const [createWhenReady, setCreateWhenReady] = useState(false);
  const [check, setCheck] = useState(null);
  const [park, setPark] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [nameError, setNameError] = useState(null);
  const fileInputRef = useRef(null);

  // Display data
  const parsed = useMemo(() => parsePinIds(pinText), [pinText]);
  const pinNotes = getParsedPinsNotes(parsed);
  const batchName = toCreationBatchName(name);
  const sopBatches = _.uniqBy(batches.filter(batch => batch.sop), 'sop');
  const run = park || check;
  const isLocked = isSending || Boolean(park);
  const isChecked = isRunFinished(check) && check.pinIds === parsed.pinIds;
  const canCheck = !isLocked && Boolean(batchName) && parsed.pinIds.length > 0;
  const sentPins = run ? _.sumBy(run.chunks.slice(0, run.nextChunk), 'length') : 0;

  const readFile = event => {
    const [file] = event.target.files;
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPinText(String(reader.result || ''));
    reader.onerror = () => window.ALERT.error(`Could not read ${file.name}.`);
    reader.readAsText(file);
    event.target.value = '';
  };

  const fillSopFrom = tag => setSop(_.find(sopBatches, { tag }).sop);

  const newRun = dryRun => ({
    tag: toCreationBatchTag(name),
    pinIds: parsed.pinIds,
    chunks: chunkPinIds(parsed.pinIds),
    sop,
    createWhenReady,
    dryRun,
    nextChunk: 0,
    report: EMPTY_PARK_REPORT
  });

  const sendRun = async start => {
    const setRun = start.dryRun ? setCheck : setPark;
    let current = { ...start, error: null };
    setRun(current);
    setNameError(null);
    setIsSending(true);
    try {
      while (current.nextChunk < current.chunks.length) {
        const { tag, chunks, nextChunk, dryRun } = current;
        const result = await parkCreationRequestPins({ tag, pinIds: chunks[nextChunk], sop: current.sop, createWhenReady: current.createWhenReady, dryRun });
        current = { ...current, nextChunk: nextChunk + 1, report: addParkResult(current.report, result) };
        setRun(current);
      }
    } catch (err) {
      // Someone else's batch took this name in the same minute, so this one starts over under another name.
      if (err.code === 'batch_name_taken') {
        setRun(null);
        setNameError(err.message);
      } else {
        setRun({ ...current, error: err.message });
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className='creation-request-start-form'>
      <div className='se-toolbar'>
        <button type='button' className='se-btn ghost' disabled={isSending} onClick={onClose}>
          ← All batches
        </button>
      </div>
      <header className='crs-heading'>
        <h2>New creation batch</h2>
        <p>
          Pins the catalog team confirmed are missing from the catalog. APM reads each link’s page with the SOP and prepares its product. Check shows
          what APM will read and roughly what it costs; nothing is parked until Start.
        </p>
      </header>

      <fieldset className='crs-fields' disabled={isLocked}>
        <div className='crs-field'>
          <span className='crs-label'>Batch name</span>
          <input type='text' aria-label='Batch name' value={name} placeholder='Hotels Q4' onChange={event => setName(event.target.value)} />
          <span className={cn('crs-hint', { error: nameError })}>{nameError || (batchName ? `Listed as ${batchName}` : 'Use letters or digits.')}</span>
        </div>

        <div className='crs-field'>
          <span className='crs-label'>Pin IDs</span>
          <textarea
            className='crs-pins'
            aria-label='Pin IDs'
            value={pinText}
            placeholder={'One pin ID per line, or separated by commas\nA .csv with a Pin ID column reads that column only'}
            onChange={event => setPinText(event.target.value)}
          />
          <div className='crs-row'>
            <button type='button' className='se-btn' onClick={() => fileInputRef.current.click()}>
              Upload .csv/.txt
            </button>
            <input ref={fileInputRef} hidden type='file' accept='.csv,.txt,text/csv,text/plain' aria-label='Pin ID file' onChange={readFile} />
            <span className='crs-hint'>{pinNotes.count}</span>
          </div>
          {pinNotes.unparsed && <span className='crs-hint warn'>{pinNotes.unparsed}</span>}
        </div>

        <div className='crs-field'>
          <div className='crs-row spread'>
            <span className='crs-label'>SOP</span>
            {sopBatches.length > 0 && (
              <select aria-label='Start from a previous batch’s SOP' value='' onChange={event => fillSopFrom(event.target.value)}>
                <option value=''>Start from a previous batch’s SOP…</option>
                {sopBatches.map(batch => (
                  <option key={batch.tag} value={batch.tag}>
                    {batch.name} · {formatDate(batch.firstParkedAt)}
                  </option>
                ))}
              </select>
            )}
          </div>
          <textarea
            className='crs-sop'
            aria-label='SOP'
            value={sop}
            maxLength={MAX_SOP_CHARS}
            placeholder='What APM should know about these pages: which name to give the product, which brand it belongs to, what to leave out.'
            onChange={event => setSop(event.target.value)}
          />
          <span className='crs-hint'>
            {formatNumber(sop.length)} / {formatNumber(MAX_SOP_CHARS)} characters
          </span>
        </div>

        <fieldset className='crs-field'>
          <legend className='crs-label'>Mode</legend>
          {MODES.map(mode => (
            <label key={mode.label} className='crs-mode'>
              <input type='radio' name='creation-mode' checked={createWhenReady === mode.createWhenReady} onChange={() => setCreateWhenReady(mode.createWhenReady)} />
              <span>
                <strong>{mode.label}</strong> {mode.description}
              </span>
            </label>
          ))}
        </fieldset>
      </fieldset>

      <div className='crs-row'>
        <button type='button' className='se-btn' disabled={!canCheck} onClick={() => sendRun(newRun(true))}>
          Check
        </button>
        <button
          type='button'
          className='se-btn primary'
          disabled={!canCheck || !isChecked}
          title={canCheck && !isChecked ? 'Check these pins first' : undefined}
          onClick={() => sendRun(newRun(false))}
        >
          Start
        </button>
      </div>

      {run && (isSending || run.error) && (
        <div className='crs-row' role='status'>
          <progress className='crs-progress' value={sentPins} max={run.pinIds.length} />
          <span>
            {run.dryRun ? 'Checked' : 'Parked'} {formatNumber(sentPins)} of {formatNumber(run.pinIds.length)} pins
          </span>
        </div>
      )}
      {run?.error && (
        <div className='crs-row crs-error' role='alert'>
          <span>{run.dryRun ? `Could not check the pins — ${run.error}` : `Parking stopped — ${run.error}`}</span>
          {!run.dryRun && (
            <button type='button' className='se-btn' onClick={() => sendRun(run)}>
              Resume
            </button>
          )}
        </div>
      )}
      {isChecked && !park && <CreationRequestParkReport report={check.report} isCheck lane={lane} chunkCount={check.chunks.length} />}
      {isRunFinished(park) && (
        <>
          <CreationRequestParkReport report={park.report} chunkCount={park.chunks.length} />
          <div className='crs-row'>
            <button type='button' className='se-btn primary' onClick={() => onOpenBatch(park.tag)}>
              Open batch
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const isRunFinished = run => Boolean(run) && !run.error && run.nextChunk === run.chunks.length;

CreationRequestStartForm.propTypes = {
  batches: PropTypes.array.isRequired,
  lane: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onOpenBatch: PropTypes.func.isRequired
};

export default CreationRequestStartForm;
